import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { Table, Input, Button, Space, Image, message, Row, Col, Popconfirm, Modal, Select, Tabs, Empty, Avatar, Typography, Spin, Divider, Tag } from "antd";
import AddShops from "../../component/AddShop";
import ShopServ from "../../service/ShopServ";
import CategoryServ from "../../service/CategoryServ";
import "../../css/shops.css";
import { useParams, useNavigate } from "react-router-dom";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  HistoryOutlined,
} from "@ant-design/icons";

import TextWithPopover from "../../util/TextWithPopover";
import { formatDate } from "../../util/dateUtils";
import Breadcrumbs from "../../component/Breadcrumbs";
import { getImageUrl } from "../../component/SafeImage";
import EmptyState from "../../util/EmptyState";
import { getApprovalRowClass } from "../../util/statusHighlight";
import { FilterBar, SecondaryFilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import FormSelect from "../../component/ui/FormSelect";
import { PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import ShopTableImage from "../../component/ui/ShopTableImage";
import TableActions from "../../component/ui/TableActions";


const { Option } = Select;

const Shop = () => {

  const { id } = useParams(); // optional shopId from URL
  const navigate = useNavigate();

  const handleViewRatings = (shop) => {
    navigate("/admin/shop-ratings", { state: { search: shop.name } });
  };

  const [search, setSearch] = useState("");
  const [zip, setZip] = useState("");
  const [shops, setShops] = useState([]);
  const [filteredShops, setFilteredShops] = useState([]);
  const [addShops, setAddShops] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [shopDetails, setShopDetails] = useState(null);
  const [sellerModal, setSellerModal] = useState({ open: false, data: null });
  const [selectedRow, setSelectedRow] = useState(null);
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedZip, setAppliedZip] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalShops, setTotalShops] = useState(0);

  // Category Filter State
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [appliedCategory, setAppliedCategory] = useState(null);
  const [status, setStatus] = useState("All");
  const [activeStatus, setActiveStatus] = useState("All");
  const [appliedActiveStatus, setAppliedActiveStatus] = useState("All");

  // Approval/Rejection States
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approveReason, setApproveReason] = useState("");
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  // Fetch all shops
  const fetchShops = async (
    targetPage = page,
    targetSearch = appliedSearch,
    targetZip = appliedZip,
    catId = appliedCategory,
    targetActiveStatus = appliedActiveStatus
  ) => {
    try {
      const filterObj = {};
      if (status !== "All") filterObj.approvalStatus = status;
      if (targetActiveStatus !== "All") filterObj.isActive = targetActiveStatus === "Active";

      const params = {
        search: targetSearch,
        zip: targetZip,
        categoryId: catId,
        filter: Object.keys(filterObj).length > 0 ? JSON.stringify(filterObj) : undefined,
      };
      const response = await ShopServ.adminIndex(params);
      const allShops = response?.data || [];
      setShops(allShops);
      setFilteredShops(allShops);
      setTotalShops(allShops.length);

      if (id) {
        const single = allShops.filter(s => (s._id || s.id) === id);
        setFilteredShops(single);
        setTotalShops(single.length);
        if (single.length > 0) {
          setSelectedRow(single[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching shops:", error);
      message.error("Failed to fetch shops");
    }
  };



  const handlePageChange = (page) => {
    setPage(page);
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const onShowSizeChange = (current, size) => {
    setPageSize(size);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchShops(page, search, zip, selectedCategory, activeStatus);
    }, 500);
    return () => clearTimeout(handler);
  }, [pageSize, page, search, zip, selectedCategory, status, activeStatus]);

  const handleTabChange = (key) => {
    setStatus(key);
    setPage(1);
  };

  const handleApprove = async () => {
    // ✅ FIX: Use shopDetails (the currently open modal item), not selectedRow
    // selectedRow could be a different record than what's displayed in the modal
    const target = shopDetails || selectedRow;
    if (!target) return;
    try {
      await ShopServ.updateStatus(target._id, "approved", approveReason);
      message.success("Shop approved successfully");
      setIsApproveModalOpen(false);
      setApproveReason("");
      // ✅ FIX: Close modal and clear stale state before refreshing list
      setShopDetails(null);
      setSelectedRow(null);
      fetchShops();
    } catch (error) {
      message.error(error.message || "Failed to approve shop");
    }
  };

  const handleReject = async () => {
    const target = shopDetails || selectedRow;
    if (!target || !rejectReason.trim()) {
      message.warning("Please provide a rejection reason");
      return;
    }
    try {
      await ShopServ.updateStatus(target._id, "rejected", rejectReason);
      message.success("Shop rejected successfully");
      setIsRejectModalOpen(false);
      setRejectReason("");
      // ✅ FIX: Close modal and clear stale state before refreshing list
      setShopDetails(null);
      setSelectedRow(null);
      fetchShops();
    } catch (error) {
      message.error(error.message || "Failed to reject shop");
    }
  };

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await CategoryServ.index({ isActive: "all", filter: JSON.stringify({ parentId: null }) });
        setCategories(res.data || []);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);




  // Fetch single shop by ID
  const fetchSingleShop = async (shopId) => {
    if (!shopId) return;
    try {
      const response = await ShopServ.single(shopId);
      setShopDetails(response);
      setSelectedRow(response); // optional: pre-select row if fetched by URL
    } catch (error) {
      console.error("Error fetching single shop:", error);
      message.error("Failed to fetch the shop");
    }
  };

  // Delete selected shop
  const handleDeleteShop = async () => {
    if (!selectedRow) {
      message.warning("Please select a shop to delete");
      return;
    }

    const shopIdToDelete = selectedRow._id || selectedRow.id;

    try {
      await ShopServ.remove(shopIdToDelete);
      message.success("Shop removed successfully");

      // Optimistic update
      setShops(prev => prev.filter(s => (s._id || s.id) !== shopIdToDelete));
      setFilteredShops(prev => prev.filter(s => (s._id || s.id) !== shopIdToDelete));

      setSelectedRow(null);

      // Sync with server
      fetchShops();
    } catch (error) {
      console.error("Error deleting shop:", error);
      message.error("Failed to remove shop");
    }
  };

  // Filter shops
  const handleFilter = () => {
    setAppliedSearch(search);
    setAppliedZip(zip);
    setAppliedCategory(selectedCategory);
    setAppliedActiveStatus(activeStatus);
    setPage(1);
    fetchShops(1, search, zip, selectedCategory, activeStatus);
  };

  // Reset filters
  const handleReset = () => {
    setSearch("");
    setZip("");
    setSelectedCategory(null);
    setActiveStatus("All");
    setPage(1);
    fetchShops(1, "", "", null, "All");
  };

  useEffect(() => {
    fetchShops();
  }, [id]);



  const columns = [
    {
      title: "Image",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: 70,
      align: "center",
      render: (url) => <ShopTableImage src={getImageUrl(url)} alt="shop" />,
    },
    {
      title: "Shop Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      render: (name, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 700, color: "#1e293b", fontSize: "13px" }}>{name}</span>
          <span style={{ fontSize: "10px", color: "#94a3b8" }}>
            ID: {record.shopId || "N/A"}
          </span>
        </div>
      ),
    },
    {
      title: "Seller",
      dataIndex: "createdBy",
      key: "seller",
      width: 180,
      render: (seller) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            size="small"
            src={seller?.imageUrl ? getImageUrl(seller?.imageUrl) : null}
            icon={<UserOutlined style={{ color: '#4776E6' }} />}
            style={{ background: '#f0f7ff', border: '1px solid #e2e8f0' }}
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#4776E6" }}>
              {seller?.name || "N/A"}
            </span>
            <span style={{ fontSize: "10px", color: "#94a3b8" }}>{seller?.email}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "approvalStatus",
      key: "status",
      width: 120,
      align: "center",
      render: (status, record) => {
        const map = {
          approved: ["#22c55e", "#f0fdf4", "#dcfce7"], // Green
          rejected: ["#ef4444", "#fef2f2", "#fee2e2"], // Red
          pending: ["#f59e0b", "#fffbeb", "#fef3c7"], // Amber
        };
        const [color, bg, border] = map[status] || map.pending;

        const isResubmitted =
          status === "pending" &&
          record.approvalHistory?.some((h) => h.status === "rejected");

        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <span
              className="status-pill"
              style={{
                padding: "4px 12px",
                borderRadius: "8px",
                fontSize: "11px",
                fontWeight: 700,
                // backgroundColor: bg,
                color: color,
                textTransform: "capitalize",
                border: `1px solid ${border}`,
                minWidth: "85px",
                textAlign: "center",
              }}
            >
              {status || "Pending"}
            </span>
            {isResubmitted && (
              <span
                className="status-pill"
                style={{
                  fontSize: "8px",
                  fontWeight: 800,
                  color: "#2563eb",
                  textTransform: "uppercase",
                }}
              >
                RESUBMITTED
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Feedback",
      key: "rating",
      width: 90,
      align: "center",
      render: (_, record) => {
        const count = record.totalRatings || 0;

        if (count === 0) {
          return (
            <span style={{ fontSize: "11px", color: "#cbd5e1" }}>
              No ratings
            </span>
          );
        }

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleViewRatings(record);
            }}
          >
            <span
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#1e293b",
                textDecoration: "underline",
              }}
            >
              ({count})
            </span>
          </div>
        );
      },
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      align: "center",
      render: (isActive) => (
        <span
          className="status-pill"
          style={{
            padding: "4px 12px",
            borderRadius: "8px",
            fontSize: "11px",
            fontWeight: 700,
            color: isActive ? "#22c55e" : "#ef4444",
            border: `1px solid ${isActive ? "#dcfce7" : "#fee2e2"}`,
            textTransform: "capitalize",
          }}
        >
          {isActive ? "Active" : "InActive"}
        </span>
      ),
    },
    {
      title: "History",
      key: "history",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Button
          type="text"
          icon={<HistoryOutlined style={{ color: "#64748b" }} />}
          onClick={(e) => {
            e.stopPropagation();
            setHistoryData(record.approvalHistory || []);
            setIsHistoryModalOpen(true);
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedRow(record);
            setShopDetails(record);
          }}
          style={{
            borderRadius: 3,
            fontWeight: 600,
            background: '#ffffffff',
            color: '#004754ff',
            border: '1px solid #e4e4e7',
            boxShadow: '0 0 0 1px #e4e4e7',
            height: 'auto',
            padding: '0px 8px',
            fontSize: 13,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const UserOutlined = (props) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );

  const statusTabs = [
    { key: "All", label: "All Shops" },
    { key: "pending", label: "Pending Approval" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
  ];

  const handleCreateShops = () => {
    setIsEditing(false);
    setShopDetails(null);
    setAddShops(true);
  };

  const handleEditShops = () => {
    if (!selectedRow) return;
    setShopDetails(selectedRow);
    setIsEditing(true);
    setAddShops(true);
  };

  const refreshShops = async (isRefresh) => {
    setAddShops(false);
    if (isRefresh) {
      setShopDetails(null);
      fetchShops();
    }
  };
  const fetchAllShops = async () => {
    try {
      const response = await ShopServ.adminIndex({ limit: 1000 });
      return Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching all shops:", error);
      return [];
    }
  };

  const topRef = React.useRef(null);

  return (
    <div className="pcu-padding-24" ref={topRef}>
      <div className="pcu-margin-bottom-16">
        <Breadcrumbs />
      </div>

      <div className="sd-container" style={{ padding: "24px", background: "white", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        {/* Header Section */}
        <div className="sd-header" style={{ marginBottom: "24px", background: "transparent", padding: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div
              className="sd-orders-icon-wrap"
              style={{
                width: "48px",
                height: "48px",
                background: "#fff",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                border: "1px solid #f1f5f9",
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4776E6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: "#1a1d2e", letterSpacing: "-0.5px" }}>Shop Management</h1>
              <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af", fontWeight: 500 }}>{totalShops} shops registered in system</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginRight: "8px" }}>
              <TableActions
                onFetchFullData={fetchAllShops}
                columns={columns}
                fileName="shops"
                pageName="Shops"
                orientation="portrait"
              />
            </div>
            <Button
              type="primary"
              onClick={handleCreateShops}
              style={{
                height: "40px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #FF8800 0%, #FF5E00 100%)",
                border: "none",
                padding: "0 20px",
                fontSize: "13px",
                fontWeight: 700,
                boxShadow: "0 4px 12px rgba(255, 136, 0, 0.3)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <PlusOutlined style={{ fontSize: "16px" }} />
              Create Shop
            </Button>
          </div>
        </div>

        {/* Status Tabs */}
        <div style={{ marginBottom: "24px", borderBottom: "1px solid #f1f5f9" }}>
          <Tabs
            defaultActiveKey="All"
            activeKey={status}
            onChange={handleTabChange}
            items={statusTabs}
            className="premium-tabs-minimal"
          />
        </div>

        <div className="pcu-padding-0">
          {/* Filters */}
          <div className="ss-filter-bar" style={{ background: "transparent", border: "none", padding: "0 0 16px 0", display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "12px" }}>
            <div className="ss-filter-field">
              <span className="ss-filter-label" style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px" }}>SEARCH SHOP</span>
              <div className="ss-search-wrap">
                <SearchOutlined className="ss-search-icon" />
                <input
                  className="ss-search-input"
                  placeholder="Name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="ss-filter-field">
              <span className="ss-filter-label" style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px" }}>ZIP CODE</span>
              <Input
                className="pcu-radius-8"
                style={{ height: "36px", border: "1px solid #e2e8f0" }}
                placeholder="ZIP..."
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>

            <div className="ss-filter-field">
              <span className="ss-filter-label" style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px" }}>CATEGORY</span>
              <Select
                placeholder="All Categories"
                style={{ width: "100%", height: "36px" }}
                value={selectedCategory}
                onChange={setSelectedCategory}
                allowClear
              >
                {categories.map((c) => (
                  <Option key={c._id} value={c._id}>{c.name}</Option>
                ))}
              </Select>
            </div>

            <div className="ss-filter-field">
              <span className="ss-filter-label" style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px" }}>ACTIVE</span>
              <Select
                placeholder="All Status"
                style={{ width: "100%", height: "36px" }}
                value={activeStatus}
                onChange={setActiveStatus}
              >
                <Option value="All">All Status</Option>
                <Option value="Active">Active</Option>
                <Option value="InActive">InActive</Option>
              </Select>
            </div>

            <div className="ss-filter-field" style={{ display: "flex", alignItems: "flex-end" }}>
              <ResetButton icon={<ReloadOutlined />} onClick={handleReset} text="Reset Filters" />
            </div>
          </div>

          <div className="pcu-border-top pcu-margin-x-n24">
            <DataTable
              columns={columns}
              data={filteredShops}
              rowKey={(record) => record._id || record.id}
              onRow={(record) => ({
                style: { cursor: 'default' },
              })}
              rowClassName={(record) => getApprovalRowClass(record.approvalStatus)}
              pagination={{
                current: page,
                pageSize: pageSize,
                total: totalShops,
                onChange: handlePageChange,
                onShowSizeChange: onShowSizeChange
              }}
            />
          </div>
        </div>

        {/* Enhanced Shop Details Modal */}
        <Modal
          open={!!shopDetails}
          onCancel={() => setShopDetails(null)}
          footer={null}
          width={600}
          centered
          className="premium-modal"
          closeIcon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>}
        >
          {shopDetails && (
            <div className="pcu-flex pcu-flex-column pcu-gap-10">
              {/* Header: Image + Title + Status */}
              <div className="pcu-flex pcu-items-center pcu-justify-between">
                <div className="pcu-flex pcu-items-center pcu-gap-12">
                  <div style={{ width: 56, height: 56, borderRadius: "10px", overflow: "hidden", border: "1px solid #f1f5f9" }}>
                    <ShopTableImage src={getImageUrl(shopDetails.imageUrl)} size={56} radius={0} />
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <h2 style={{ fontSize: "16px", fontWeight: 800, margin: 0, color: "#1a1d2e" }}>{shopDetails.name}</h2>
                      {shopDetails.approvalStatus === "approved" ? (
                        <Tag color="success" style={{ borderRadius: "5px", fontSize: "9px", fontWeight: 700, padding: "0 6px" }}>APPROVED</Tag>
                      ) : shopDetails.approvalStatus === "rejected" ? (
                        <Tag color="error" style={{ borderRadius: "5px", fontSize: "9px", fontWeight: 700, padding: "0 6px" }}>REJECTED</Tag>
                      ) : (
                        <Tag color="warning" style={{ borderRadius: "5px", fontSize: "9px", fontWeight: 700, padding: "0 6px" }}>PENDING</Tag>
                      )}
                    </div>
                    <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "1px" }}>
                      ID: {shopDetails.shopId} <span style={{ margin: "0 4px" }}>•</span> {shopDetails.business?.category || "Shop"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Seller Info Section */}
              <div style={{ background: "white", border: "1px solid #f1f5f9", borderRadius: "8px", padding: "8px 12px", marginBottom: "0px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                  <div style={{ fontSize: "10px", fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px" }}>Seller Details</div>
                  <span style={{ fontSize: "9px", fontWeight: 700, color: "#4776E6", background: "#f0f7ff", padding: "2px 6px", borderRadius: "4px" }}>OWNER</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Avatar
                    size={28}
                    src={shopDetails.createdBy?.imageUrl ? getImageUrl(shopDetails.createdBy?.imageUrl) : null}
                    icon={<UserOutlined style={{ color: '#4776E6' }} />}
                    style={{ border: "1px solid #e2e8f0", background: '#f8fafc' }}
                  />
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", lineHeight: "1.2" }}>{shopDetails.createdBy?.name || "N/A"}</div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>{shopDetails.createdBy?.email} <span style={{ margin: "0 4px" }}>•</span> {shopDetails.createdBy?.phone || "No Phone"}</div>
                  </div>
                </div>
              </div>

              {/* Detail Grid */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
              }}>
                {/* Location - Full Width */}
                <div style={{ gridColumn: "1 / -1", background: "#f8fafc", borderRadius: "8px", padding: "6px 12px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>Location</div>
                  <div style={{ fontSize: "12px", color: "#374151", fontWeight: 500 }}>
                    {shopDetails.location
                      ? `${shopDetails.location.address || ""}, ${shopDetails.location.city || ""}, ${shopDetails.location.state || ""} ${shopDetails.location.zipCode || ""}`
                      : "No location provided"}
                  </div>
                </div>

                {/* Opening & Closing Time */}
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "6px 12px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>Opening Time</div>
                  <div style={{ fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>
                    {shopDetails.business?.openingTime
                      ? dayjs(shopDetails.business.openingTime, "HH:mm:ss").format("hh:mm A")
                      : "N/A"}
                  </div>
                </div>
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "6px 12px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>Closing Time</div>
                  <div style={{ fontSize: "12px", color: "#dc2626", fontWeight: 600 }}>
                    {shopDetails.business?.closingTime
                      ? dayjs(shopDetails.business.closingTime, "HH:mm:ss").format("hh:mm A")
                      : "N/A"}
                  </div>
                </div>

                {/* Phone & Email */}
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "6px 12px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>Phone</div>
                  <div style={{ fontSize: "12px", color: "#374151", fontWeight: 500 }}>
                    {shopDetails.contact?.phone || "N/A"}
                  </div>
                </div>
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "6px 12px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>Email</div>
                  <div style={{ fontSize: "12px", color: "#374151", fontWeight: 500 }}>
                    {shopDetails.contact?.email || "N/A"}
                  </div>
                </div>

                {/* GST & License Number */}
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "6px 12px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>GST Number</div>
                  <div style={{ fontSize: "12px", color: "#374151", fontWeight: 500 }}>
                    {shopDetails.business?.gstNumber || "N/A"}
                  </div>
                </div>
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "6px 12px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>Shop License Number</div>
                  <div style={{ fontSize: "12px", color: "#374151", fontWeight: 500 }}>
                    {shopDetails.business?.shopLicenseNumber || "N/A"}
                  </div>
                </div>

                {/* Status & Approval/Rejection Message */}
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "6px 12px", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "3px", letterSpacing: "0.5px" }}>Status</div>
                  <Tag
                    color={
                      shopDetails.approvalStatus === "approved"
                        ? "green"
                        : shopDetails.approvalStatus === "rejected"
                          ? "red"
                          : "orange"
                    }
                    style={{ margin: 0, fontWeight: 700, fontSize: "10px", padding: "0 6px" }}
                  >
                    {(shopDetails.approvalStatus || "pending").toUpperCase()}
                  </Tag>
                </div>

                {shopDetails.approvalStatus === "approved" && (() => {
                  const lastApproval = shopDetails.approvalHistory?.slice().reverse().find(h => h.status === "approved");
                  const msg = lastApproval?.reason;
                  if (msg && msg !== "Shop approved by admin") {
                    return (
                      <div style={{ background: "#f0fdf4", borderRadius: "8px", padding: "6px 12px", border: "1px solid #dcfce7" }}>
                        <div style={{ fontSize: "9px", fontWeight: 700, color: "#16a34a", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>Approval Message</div>
                        <div style={{ fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>{msg}</div>
                      </div>
                    );
                  }
                  return <div />;
                })()}

                {shopDetails.approvalStatus === "rejected" && (
                  <div style={{ background: "#fff1f2", borderRadius: "8px", padding: "6px 12px", border: "1px solid #fecdd3" }}>
                    <div style={{ fontSize: "9px", fontWeight: 700, color: "#e11d48", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>Rejection Reason</div>
                    <div style={{ fontSize: "12px", color: "#e11d48", fontWeight: 600 }}>{shopDetails.rejectionReason || "No reason provided"}</div>
                  </div>
                )}

                {/* Rating */}
                <div style={{ background: "#fffbeb", borderRadius: "8px", padding: "6px 12px", border: "1px solid #fef3c7" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "3px", letterSpacing: "0.5px" }}>Avg Rating</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {shopDetails.averageRating > 0 ? (
                      <>
                        {[1, 2, 3, 4, 5].map(star => (
                          <svg key={star} width="13" height="13" viewBox="0 0 24 24"
                            fill={star <= Math.round(shopDetails.averageRating) ? "#f59e0b" : "#e2e8f0"}
                            xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                        ))}
                        <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", marginLeft: 2 }}>
                          {(shopDetails.averageRating || 0).toFixed(1)}
                        </span>
                      </>
                    ) : (
                      <span style={{ fontSize: "12px", color: "#94a3b8" }}>No ratings yet</span>
                    )}
                  </div>
                </div>

                {/* Total Ratings */}
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "6px 12px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>Total Reviews</div>
                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
                    {shopDetails.totalRatings || 0}
                    <span style={{ fontSize: "11px", fontWeight: 500, color: "#64748b", marginLeft: 4 }}>reviews</span>
                  </div>
                </div>

                {/* Description - Full Width */}
                <div style={{ gridColumn: "1 / -1", background: "#f8fafc", borderRadius: "8px", padding: "6px 12px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: "1px", letterSpacing: "0.5px" }}>Description</div>
                  <div style={{ fontSize: "12px", color: "#374151", fontWeight: 500, lineHeight: "1.4" }}>
                    {shopDetails.description || "No description provided"}
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div style={{ paddingTop: "12px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  {shopDetails.approvalStatus !== "approved" && (
                    <Button
                      onClick={() => setIsApproveModalOpen(true)}
                      style={{ height: "36px", borderRadius: "8px", color: "#16a34a", border: "1px solid #dcfce7", background: "#f0fdf4", fontWeight: 700, fontSize: "13px" }}
                    >
                      Approve
                    </Button>
                  )}
                  {shopDetails.approvalStatus !== "rejected" && (
                    <Button
                      onClick={() => setIsRejectModalOpen(true)}
                      style={{ height: "36px", borderRadius: "8px", color: "#dc2626", border: "1px solid #fee2e2", background: "#fef2f2", fontWeight: 700, fontSize: "13px" }}
                    >
                      Reject
                    </Button>
                  )}
                  <Button
                    type="text"
                    icon={<HistoryOutlined style={{ color: "#64748b" }} />}
                    onClick={() => {
                      setHistoryData(shopDetails.approvalHistory || []);
                      setIsHistoryModalOpen(true);
                    }}
                    style={{ height: "36px", color: "#64748b", fontWeight: 600, fontSize: "13px", display: "flex", alignItems: "center" }}
                  >
                    History
                  </Button>
                  <Button
                    type="text"
                    icon={<EditOutlined style={{ color: "#4776E6" }} />}
                    onClick={handleEditShops}
                    style={{ height: "36px", color: "#4776E6", fontWeight: 600, fontSize: "13px", display: "flex", alignItems: "center" }}
                  >
                    Edit
                  </Button>
                </div>

                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <Popconfirm
                    title="Remove Shop"
                    description="Are you sure you want to delete this shop?"
                    onConfirm={handleDeleteShop}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      icon={<DeleteOutlined />}
                      style={{ height: "36px", borderRadius: "8px", color: "#ef4444", border: "1px solid #fee2e2", fontWeight: 600, fontSize: "13px" }}
                    > Remove </Button>
                  </Popconfirm>
                  <Button
                    onClick={() => setShopDetails(null)}
                    style={{ height: "36px", borderRadius: "8px", color: "#1a1d2e", border: "1px solid #e2e8f0", fontWeight: 600, fontSize: "13px", padding: "0 20px" }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {addShops && (
          <AddShops
            open={addShops}
            refreshShops={refreshShops}
            shopDetails={shopDetails || {}}
            isEditing={isEditing}
          />
        )}

        <Modal
          title="Seller Details"
          open={sellerModal.open}
          onCancel={() => setSellerModal({ open: false, data: null })}
          footer={[
            <Button key="close" onClick={() => setSellerModal({ open: false, data: null })}>
              Close
            </Button>
          ]}
        >
          {sellerModal.data ? (
            <div style={{ padding: '10px 0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <div style={{ color: '#8c8c8c', fontSize: '12px' }}>FULL NAME</div>
                  <div style={{ fontSize: '16px', fontWeight: '600' }}>{sellerModal.data.name}</div>
                </div>
                <div>
                  <div style={{ color: '#8c8c8c', fontSize: '12px' }}>EMAIL ADDRESS</div>
                  <div style={{ fontSize: '15px' }}>{sellerModal.data.email}</div>
                </div>
                <div>
                  <div style={{ color: '#8c8c8c', fontSize: '12px' }}>PHONE NUMBER</div>
                  <div style={{ fontSize: '15px' }}>{sellerModal.data.phone || 'Not Provided'}</div>
                </div>
                <div>
                  <div style={{ color: '#8c8c8c', fontSize: '12px' }}>SELLER ID</div>
                  <div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{sellerModal.data.userId || sellerModal.data._id}</div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <EmptyState
                title="No Details"
                description="No seller information available"
                gradient="orange"
                showAction={false}
                icon="inbox"
                size="small"
              />
            </div>
          )}
        </Modal>

        {/* Reject Reason Modal */}
        <Modal
          title="Reject Shop"
          open={isRejectModalOpen}
          onOk={handleReject}
          onCancel={() => setIsRejectModalOpen(false)}
          okText="Confirm Reject"
          okButtonProps={{ danger: true }}
        >
          <div className="pcu-margin-bottom-16">
            <p className="pcu-font-14 pcu-color-text">
              Please provide a reason for rejecting <strong>{(shopDetails || selectedRow)?.name}</strong>.
            </p>
            <Input.TextArea
              rows={4}
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="pcu-radius-8"
            />
          </div>
        </Modal>

        {/* Approve Reason Modal */}
        <Modal
          title="Approve Shop"
          open={isApproveModalOpen}
          onOk={handleApprove}
          onCancel={() => setIsApproveModalOpen(false)}
          okText="Confirm Approve"
          okButtonProps={{ style: { backgroundColor: "#16a34a", borderColor: "#16a34a" } }}
        >
          <div className="pcu-margin-bottom-16">
            <p className="pcu-font-14 pcu-color-text">
              Would you like to send an approval message to <strong>{(shopDetails || selectedRow)?.name}</strong>? (Optional)
            </p>
            <Input.TextArea
              rows={4}
              placeholder="Enter welcome message or approval notes..."
              value={approveReason}
              onChange={(e) => setApproveReason(e.target.value)}
              className="pcu-radius-8"
            />
          </div>
        </Modal>

        {/* History Modal */}
        <Modal
          title="Approval History"
          open={isHistoryModalOpen}
          onCancel={() => setIsHistoryModalOpen(false)}
          footer={null}
          width={600}
        >
          <div className="pcu-padding-y-16">
            {historyData && historyData.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {historyData
                  .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                  .map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: "12px",
                        borderLeft: `4px solid ${item.status === "approved"
                          ? "#22c55e"
                          : item.status === "rejected"
                            ? "#ef4444"
                            : "#f59e0b"
                          }`,
                        backgroundColor: "#f8fafc",
                        borderRadius: "0 8px 8px 0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <span style={{ fontWeight: 700, textTransform: "capitalize" }}>
                          {item.status}
                        </span>
                        <span style={{ fontSize: "12px", color: "#64748b" }}>
                          {formatDate(item.updatedAt)}
                        </span>
                      </div>
                      <div style={{ fontSize: "13px", color: "#334155" }}>{item.reason}</div>
                      {item.changedFields && item.changedFields.length > 0 && (
                        <div
                          style={{ fontSize: "11px", color: "#94a3b8", marginTop: "8px" }}
                        >
                          Changed: {item.changedFields.join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <Empty description="No history available" />
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Shop;
